import React from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import {
  makeZoneDeviceSelectionKey,
  mergeEligibleDevicesWithAssigned,
} from "../zoneEligibleDevicesFromPosWorkspaces";

const ZONES_I18N = "pages.dsokDetails.accessSystem.zones";

function buildSelectableKeySet(selectableDevices) {
  const s = new Set();
  (selectableDevices || []).forEach((row) => {
    if (row && row.selectionKey) s.add(row.selectionKey);
  });
  return s;
}

function validateDevicesAgainstSelectable(values, selectableDevices) {
  const allowed = buildSelectableKeySet(selectableDevices);
  const list = Array.isArray(values.devices) ? values.devices : [];
  for (let i = 0; i < list.length; i += 1) {
    const d = list[i];
    const k = makeZoneDeviceSelectionKey(d.workspaceId, d.deviceId);
    if (!allowed.has(k)) {
      return i;
    }
  }
  return -1;
}

const ZoneForm = ({
  mode,
  initialValues,
  onSubmit,
  onCancel,
  /** Turnstile/gate rows from POS workspace API only (add picker). */
  posEligibleDevices,
  devicesLoading,
  devicesLoadError,
  saving,
}) => {
  const { t } = useTranslation();

  const validationSchema = React.useMemo(
    () =>
      Yup.object({
        name: Yup.string().trim().required(
          t(`${ZONES_I18N}.form.validation.nameRequired`, {
            defaultValue: "Name is required.",
          }),
        ),
        description: Yup.string(),
        isActive: Yup.boolean(),
        devices: Yup.array().of(
          Yup.object({
            deviceId: Yup.string().required(),
            workspaceId: Yup.string().required(),
            deviceType: Yup.string().required(),
            isActive: Yup.boolean(),
          }),
        ),
      }),
    [t],
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      validate={(values) => {
        const errors = {};
        if (devicesLoadError) return errors;
        const merged = mergeEligibleDevicesWithAssigned(
          posEligibleDevices,
          values.devices,
        );
        const badIdx = validateDevicesAgainstSelectable(values, merged);
        if (badIdx >= 0) {
          errors.devices = t(`${ZONES_I18N}.form.validation.invalidDevice`, {
            defaultValue:
              "One or more devices are not valid for this customer.",
          });
        }
        return errors;
      }}
      onSubmit={onSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        isSubmitting,
      }) => {
        const assignedKeys = new Set(
          (values.devices || []).map((d) =>
            makeZoneDeviceSelectionKey(d.workspaceId, d.deviceId),
          ),
        );
        const mergedCatalog = mergeEligibleDevicesWithAssigned(
          posEligibleDevices,
          values.devices,
        );
        const pickerRows = posEligibleDevices || [];
        const availableToAdd = pickerRows.filter(
          (opt) => !assignedKeys.has(opt.selectionKey),
        );

        const disableSave = saving || isSubmitting || devicesLoading;

        return (
          <Form>
            <Stack spacing={2}>
              {devicesLoadError ? (
                <Alert severity="warning">
                  {t(`${ZONES_I18N}.form.devicesLoadError`, {
                    defaultValue:
                      "Could not load POS workspace devices. You can still edit name and status; device assignment may be unavailable.",
                  })}
                </Alert>
              ) : null}

              <TextField
                name="name"
                label={t(`${ZONES_I18N}.form.fields.name`, {
                  defaultValue: "Name",
                })}
                value={values.name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
                fullWidth
                error={Boolean(touched.name && errors.name)}
                helperText={touched.name && errors.name ? errors.name : " "}
              />

              <TextField
                name="description"
                label={t(`${ZONES_I18N}.form.fields.description`, {
                  defaultValue: "Description",
                })}
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                fullWidth
                multiline
                minRows={2}
                helperText=" "
              />

              <FormControlLabel
                control={
                  <Switch
                    name="isActive"
                    checked={Boolean(values.isActive)}
                    onChange={(e) =>
                      setFieldValue("isActive", e.target.checked)
                    }
                  />
                }
                label={t(`${ZONES_I18N}.form.fields.isActive`, {
                  defaultValue: "Zone active",
                })}
              />

              <Typography variant="subtitle1">
                {t(`${ZONES_I18N}.form.devicesSection`, {
                  defaultValue: "Devices (turnstile / gate)",
                })}
              </Typography>

              {devicesLoading ? (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={22} />
                  <Typography variant="body2" color="text.secondary">
                    {t(`${ZONES_I18N}.form.devicesLoading`, {
                      defaultValue: "Loading devices…",
                    })}
                  </Typography>
                </Box>
              ) : !devicesLoadError && availableToAdd.length === 0 && (values.devices || []).length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t(`${ZONES_I18N}.form.noEligibleDevices`, {
                    defaultValue:
                      "No turnstile or gate devices found in POS workspaces.",
                  })}
                </Typography>
              ) : null}

              {!devicesLoading && !devicesLoadError && availableToAdd.length > 0 ? (
                <Autocomplete
                  key={`add-${(values.devices || []).length}`}
                  options={availableToAdd}
                  getOptionLabel={(opt) =>
                    `${opt.workspaceName} — ${opt.deviceName} (${opt.deviceId})`
                  }
                  isOptionEqualToValue={(a, b) =>
                    a.selectionKey === b.selectionKey
                  }
                  onChange={(_e, opt) => {
                    if (!opt) return;
                    const key = opt.selectionKey;
                    if (assignedKeys.has(key)) return;
                    const next = [
                      ...(values.devices || []),
                      {
                        deviceId: opt.deviceId,
                        workspaceId: opt.workspaceId,
                        deviceType: opt.deviceType,
                        isActive: true,
                        device:
                          opt.device && typeof opt.device === "object"
                            ? { ...opt.device, isActive: true }
                            : undefined,
                      },
                    ];
                    setFieldValue("devices", next);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t(`${ZONES_I18N}.form.addDevice`, {
                        defaultValue: "Add device",
                      })}
                      helperText={t(`${ZONES_I18N}.form.addDeviceHint`, {
                        defaultValue:
                          "Only turnstile and gate devices from POS workspaces are listed.",
                      })}
                    />
                  )}
                />
              ) : null}

              {errors.devices && typeof errors.devices === "string" ? (
                <Alert severity="error">{errors.devices}</Alert>
              ) : null}

              <FieldArray name="devices">
                {({ remove }) => (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          {t(`${ZONES_I18N}.form.table.workspace`, {
                            defaultValue: "Workspace",
                          })}
                        </TableCell>
                        <TableCell>
                          {t(`${ZONES_I18N}.form.table.deviceName`, {
                            defaultValue: "Device",
                          })}
                        </TableCell>
                        <TableCell>
                          {t(`${ZONES_I18N}.form.table.deviceId`, {
                            defaultValue: "Device ID",
                          })}
                        </TableCell>
                        <TableCell>
                          {t(`${ZONES_I18N}.form.table.deviceType`, {
                            defaultValue: "Type",
                          })}
                        </TableCell>
                        <TableCell align="center">
                          {t(`${ZONES_I18N}.form.table.deviceActive`, {
                            defaultValue: "Active",
                          })}
                        </TableCell>
                        <TableCell align="right">
                          {t(`${ZONES_I18N}.form.table.actions`, {
                            defaultValue: "",
                          })}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(values.devices || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6}>
                            <Typography variant="body2" color="text.secondary">
                              {t(`${ZONES_I18N}.form.noDevicesAssigned`, {
                                defaultValue: "No devices assigned.",
                              })}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        (values.devices || []).map((d, idx) => {
                          const meta = (mergedCatalog || []).find(
                            (s) => s.selectionKey === makeZoneDeviceSelectionKey(
                              d.workspaceId,
                              d.deviceId,
                            ),
                          );
                          const wsName =
                            meta?.workspaceName || d.workspaceId || "—";
                          const devName = meta?.deviceName || d.deviceId || "—";
                          return (
                            <TableRow key={`${d.workspaceId}-${d.deviceId}-${idx}`}>
                              <TableCell>{wsName}</TableCell>
                              <TableCell>{devName}</TableCell>
                              <TableCell>{d.deviceId}</TableCell>
                              <TableCell>{d.deviceType}</TableCell>
                              <TableCell align="center">
                                <Switch
                                  size="small"
                                  checked={d.isActive !== false}
                                  onChange={(e) => {
                                    const checked = e.target.checked;
                                    setFieldValue(
                                      `devices[${idx}].isActive`,
                                      checked,
                                    );
                                    if (d.device && typeof d.device === "object") {
                                      setFieldValue(
                                        `devices[${idx}].device.isActive`,
                                        checked,
                                      );
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => remove(idx)}
                                >
                                  {t("buttons.remove", {
                                    defaultValue: "Remove",
                                  })}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                )}
              </FieldArray>

              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button onClick={onCancel} disabled={saving}>
                  {t("buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={disableSave}
                >
                  {saving ? (
                    <CircularProgress size={22} color="inherit" />
                  ) : mode === "create" ? (
                    t("buttons.create", { defaultValue: "Create" })
                  ) : (
                    t("buttons.save")
                  )}
                </Button>
              </Stack>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ZoneForm;
